import { isEqual, throttle } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef, useState } from 'react';

import { StyledTooltip } from './styles';

const TOP = 'top';
const LEFT = 'left';
const BOTTOM = 'bottom';
const RIGHT = 'right';

const initialPosition = { left: 0, top: 0 };

// get coordinates of the tooltip and the target element
const getTipPosition = ({ offset, parent, position, tip }) => {
  const scrollY =
    window.scrollY !== undefined ? window.scrollY : window.pageYOffset;
  const scrollX =
    window.scrollX !== undefined ? window.scrollX : window.pageXOffset;

  const pNode = parent.current.getBoundingClientRect();
  const tipNode = tip.current.getBoundingClientRect();

  const obj = { ...initialPosition };

  switch (position) {
    case TOP:
      obj.top = scrollY + pNode.top - tipNode.height - offset;
      obj.left = scrollX + pNode.left + pNode.width / 2 - tipNode.width / 2;
      break;

    case LEFT:
      obj.top = scrollY + pNode.top + pNode.height / 2 - tipNode.height / 2;
      obj.left = scrollX + pNode.left - offset - tipNode.width;
      break;

    case BOTTOM:
      obj.top = scrollY + pNode.top + pNode.height + offset;
      obj.left = scrollX + pNode.left + pNode.width / 2 - tipNode.width / 2;
      break;

    case RIGHT:
      obj.top = scrollY + pNode.top + pNode.height / 2 - tipNode.height / 2;
      obj.left = scrollX + pNode.left + pNode.width + offset;
      break;

    default:
  }

  return obj;
};

const Tooltip = ({
  children,
  className,
  offset,
  parent,
  position,
  variant,
  maxWidth,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [positionObject, setPositionObject] = useState(initialPosition);
  const tip = useRef(null);

  // if there are no refs for the parent or tooltip then dont set position
  useLayoutEffect(() => {
    if (tip?.current && parent?.current)
      setPositionObject(getTipPosition({ offset, parent, position, tip }));

    // on scroll or resize update the tooltip position if its changed
    const handleUpdatePositionObject = () => {
      if (tip?.current && parent?.current) {
        const currentPosition = getTipPosition({
          offset,
          parent,
          position,
          tip,
        });
        if (!isEqual(currentPosition, positionObject))
          setPositionObject(currentPosition);
      }
    };

    // throttle the scroll position so it doesn't spam
    const throttledHandlePositionObject = throttle(
      handleUpdatePositionObject,
      250,
    );

    window.addEventListener('scroll', throttledHandlePositionObject, true);
    window.addEventListener('resize', throttledHandlePositionObject);
    return () => {
      window.removeEventListener('scroll', throttledHandlePositionObject, true);
      window.addEventListener('resize', throttledHandlePositionObject);
    };
  }, []); // eslint-disable-line
  // TODO: re-evaluate use of useLayoutEffect with useEffect dependencies

  return (
    <StyledTooltip
      className={className}
      direction={position}
      maxWidth={maxWidth}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      position={positionObject}
      ref={tip}
      variant={variant}
    >
      {children}
    </StyledTooltip>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  maxWidth: PropTypes.number.isRequired,
  offset: PropTypes.number,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  parent: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  position: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'arrow']).isRequired,
};

Tooltip.defaultProps = {
  className: undefined,
  offset: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  position: undefined,
};

export default Tooltip;
